import React, { useState, useRef, useEffect } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { SplitButton } from 'primereact/splitbutton';
import './GalleryArticleForm.css';
import { MAX_IMAGES_PER_SECTION, MAX_SECTIONS, MAX_DESCRIPTION_LENGTH } from './constant';
import ArticleMetadata from './ArticleMetadata';
import { Tooltip } from 'primereact/tooltip';
import { TabView, TabPanel } from 'primereact/tabview';
import { Divider } from 'primereact/divider';
import { Formik, Field, Form } from 'formik';

const GalleryArticleForm = ({ onClose, onSave, values, setFieldValue, formik }) => {
    const toast = useRef(null);
    const [activeSection, setActiveSection] = useState(0);
    const [selectedStatus, setSelectedStatus] = useState(values.status || 'DRAFT');

    const handleStatusChange = (status) => {
        setSelectedStatus(status);
        setFieldValue('status', status);
    };

    const handleSubmit = () => {
        onSave()
    };

    const statusItems = [
        {
            label: 'Save as Draft',
            icon: 'pi pi-save',
            command: () => handleStatusChange('DRAFT')
        },
        {
            label: 'Publish',
            icon: 'pi pi-check',
            command: () => handleStatusChange('PUBLISHED')
        }
    ];

    useEffect(() => {
        setFieldValue('status', selectedStatus);
    }, [selectedStatus, setFieldValue]);

    const handleAddSection = () => {
        if (values.sections.length < MAX_SECTIONS) {
            const newSection = { id: Date.now(), images: [], description: '' };
            setFieldValue('sections', [...values.sections, newSection]);
            setActiveSection(values.sections.length);
        }
    };

    const handleRemoveSection = (sectionId) => {
        if (values.sections.length <= 1) return;

        setFieldValue('sections', values.sections.filter(section => section.id !== sectionId));
        // If we're removing the active section, update activeSection
        const currentIndex = values.sections.findIndex(s => s.id === sectionId);
        if (currentIndex === activeSection && currentIndex > 0) {
            setActiveSection(currentIndex - 1);
        } else if (currentIndex < activeSection) {
            setActiveSection(activeSection - 1);
        }
    };

    const handleImageUpload = (e, sectionId) => {
        const files = Array.from(e.files);

        if (files.length > MAX_IMAGES_PER_SECTION) {
            toast.current.show({ severity: 'warn', summary: 'Limit Reached', detail: `You can only upload ${MAX_IMAGES_PER_SECTION} images per section` });
            return;
        }

        const index = values.sections.findIndex(section => section.id === sectionId);
        if (index !== -1) {
            const newSections = [...values.sections];
            newSections[index].images = [...newSections[index].images, ...files.map(file => ({
                id: Date.now() + Math.random(),
                url: URL.createObjectURL(file),
                file
            }))];
            setFieldValue('sections', newSections);
        }
    };

    const handleDescriptionChange = (e, sectionId) => {
        if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
            const index = values.sections.findIndex(section => section.id === sectionId);
            if (index !== -1) {
                const newSections = [...values.sections];
                newSections[index].description = e.target.value;
                setFieldValue('sections', newSections);
            }
        }
    };

    const handleRemoveImage = (sectionId, imageId) => {
        const index = values.sections.findIndex(section => section.id === sectionId);
        if (index !== -1) {
            const newSections = [...values.sections];
            newSections[index].images = newSections[index].images.filter(img => img.id !== imageId);
            setFieldValue('sections', newSections);
        }
    };

    // Remove custom handleSubmit since Formik handles it

    const handleTagSelect = (e) => {
        const selected = e.value;
        const currentTags = values.tags || [];
        if (!currentTags.includes(selected)) {
            const newTags = [...currentTags, selected];
            setFieldValue('tags', newTags);
        }
    };

    const handleTagRemove = (tagToRemove) => {
        const currentTags = values.tags || [];
        const newTags = currentTags.filter(tag => tag !== tagToRemove);
        setFieldValue('tags', newTags);
    };

    return (
        <div className="w-full">
            <Toast ref={toast} />
            <Tooltip target=".add-section-btn" content="Add new carousel section" />

            <Card className="w-full">
                <div className="flex flex-column gap-2">
                    <ArticleMetadata
                        compact
                        toastRef={toast}
                        maxTitleLength={100}
                        maxExcerptLength={200}
                        maxTags={5}
                        maxTagLength={20}
                    />

                    <div className="mt-3">
                        <div className="flex align-items-center gap-2 mb-3">
                            <h3 className="m-0">Carousel Section</h3>
                            <small className="text-500">(Each tab represents a carousel section)</small>
                        </div>

                        <TabView
                            activeIndex={activeSection}
                            onTabChange={(e) => setActiveSection(e.index)}
                            renderActiveOnly={false}
                            className={values.sections.length === 1 ? 'single-tab' : ''}
                        >
                            {values.sections.map((section, index) => (
                                <TabPanel
                                    key={section.id}
                                    header={
                                        <div className="p-tabview-title flex align-items-center gap-2">
                                            <span>Collection {index + 1}</span>
                                            {values.sections.length > 1 && (
                                                <Button
                                                    icon="pi pi-times"
                                                    className="p-button-text p-button-sm p-button-rounded p-button-danger"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveSection(section.id);
                                                    }}
                                                />
                                            )}
                                        </div>
                                    }
                                    className="p-tabview-panel"
                                    headerClassName="p-tabview-nav-link"
                                >
                                    <span className="block mb-2">
                                        <small className="text-500">
                                            Add up to {MAX_IMAGES_PER_SECTION} images for this carousel section
                                        </small>
                                    </span>

                                    <div className="mb-3">
                                        <FileUpload
                                            mode="basic"
                                            name="images[]"
                                            accept="image/*"
                                            auto
                                            maxFileSize={1000000}
                                            multiple
                                            onSelect={(e) => handleImageUpload(e, section.id)}
                                            chooseLabel={`Add Images (${section.images.length}/${MAX_IMAGES_PER_SECTION})`}
                                            className="w-full p-button-primary"
                                            disabled={section.images.length >= MAX_IMAGES_PER_SECTION}
                                        />

                                        <div className="flex flex-wrap gap-2 mt-4">
                                            {section.images.map((img) => (
                                                <div key={img.id} className="relative w-8rem h-8rem" style={{backgroundColor: '#f5f5f5'}}>
                                                    <div className="w-full h-full flex align-items-center justify-content-center border-round overflow-hidden">
                                                        <Image
                                                            src={img.url}
                                                            alt="Gallery image"
                                                            className="max-w-full max-h-full"
                                                            preview
                                                            imageStyle={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                                            onError={(e) => {
                                                                e.target.src = '/placeholder-image.png';
                                                            }}
                                                        />
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        icon="pi pi-times"
                                                        className="p-button-rounded p-button-danger p-button-sm absolute"
                                                        style={{ top: '-0.5rem', right: '-0.5rem', transform: 'scale(70%, 70%)' }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRemoveImage(section.id, img.id);
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <div className="flex justify-content-between align-items-center mb-1">
                                            <label htmlFor={`description-${index}`} className="block text-sm">
                                                Carousel Description (optional)
                                            </label>
                                            <small className="text-500">
                                                {section.description?.length || 0}/{MAX_DESCRIPTION_LENGTH}
                                            </small>
                                        </div>
                                        <Field
                                            name={`sections[${index}].description`}
                                            as={InputTextarea}
                                            rows={2}
                                            className="w-full text-sm"
                                            placeholder="Add a description for this carousel section..."
                                            maxLength={MAX_DESCRIPTION_LENGTH}
                                            value={section.description || ''}
                                            onKeyDown={(e) => {
                                                if (e.target.value.length >= MAX_DESCRIPTION_LENGTH && e.key !== 'Backspace' && e.key !== 'Delete') {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </div>
                                </TabPanel>
                            ))}
                            {values.sections.length < MAX_SECTIONS && (
                                <TabPanel
                                    header={
                                        <div className='flex align-items-center gap-6'>
                                            <Divider layout='vertical' />
                                            <div className="p-tabview-title flex align-items-center gap-2">
                                                <span>Add Collection</span>
                                                <i className="pi pi-plus" />
                                            </div>
                                        </div>
                                    }
                                    className="p-tabview-panel add-carousel-tab"
                                    headerClassName="p-tabview-nav-link"
                                >
                                    <div className="flex justify-content-center align-items-center p-4">
                                        <Button
                                            icon="pi pi-plus"
                                            label="Add new collection"
                                            className="p-button-text"
                                            onClick={handleAddSection}
                                            type='button'
                                        />
                                    </div>
                                </TabPanel>
                            )}
                        </TabView>
                    </div>
                    <Divider />
                    <div className="flex flex-column gap-2">
                        <small className="text-500">
                            {values.sections.length} of {MAX_SECTIONS} carousels created • {values.sections[activeSection]?.images.length || 0} images in current carousel
                        </small>
                        <div className="flex gap-2 w-full">
                            <Button
                                label="Cancel"
                                onClick={onClose}
                                className="p-button-text p-button-sm flex-1"
                                type="button"
                            />
                            <SplitButton
                                label={selectedStatus === 'DRAFT' ? 'Save as Draft' :
                                    selectedStatus === 'PUBLISHED' ? 'Publish' : 'Schedule'}
                                icon={selectedStatus === 'PUBLISHED' ? 'pi pi-check' : 'pi pi-save'}
                                onClick={handleSubmit}
                                model={statusItems}
                                className="p-button-primary p-button-sm flex-1"
                                type="button"
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default GalleryArticleForm;
